import { Worker } from "./worker.type";

export type Appraisal = {
  id: number;
  company_id?: string;
  goal_name?: string;
  date_start?: Date;
  date_end?: Date;
  kpi?: number;
  kgi?: number;
  status?: "opened" | "published";
  appraisal_kpis?: AppraisalKpi[];
  appraisal_kgis?: AppraisalKgi[];
  appraisal_members?: AppraisalMember[];
  appraisal_setting?: AppraisalSetting;
  appraisal_orgs?: AppraisalOrgs[];
  count_published?: number;
  appraisal_status_info?: {
    appraisal_members?: number;
    appraisal_sheets_count?: number;
  };
};

export type AppraisalSetting = {
  id: number;
  company_id?: string;
  max_kpi_score?: number;
  max_kgi_score?: number;
  max_number_appraiseres?: number;
  tab_kpi_name?: string;
  tab_kgi_name?: string;
  require_approver_mid_term?: boolean;
  reflect_previous_appraiser_comment?: boolean;
  salary_ranks?: {
    appraisal_rank: string;
    min_point: number;
    max_point: number;
    salary_coefficient: number;
  }[];
};

export type AppraisalOrgs = {
  id: number;
  appraisal_id?: number;
  organizable_id?: number;
  parent_organizable_id?: number;
  organizable_type?: "org_nodes" | "orgs";
};

export type AppraisalMember = {
  id?: number;
  appraisal_id?: number;
  worker_id?: number;
  appraisal_appraisers?: AppraisalAppraisere[];
  appraisal_sheet?: {
    id?: number;
    deleted_at?: Date;
  };
};

export type AppraisalKgi = {
  id?: number;
  appraisal_id?: number;
  kgi_name?: string;
  kgi_priority?: number;
  kgi_goal?: string;
  kgi_note?: string;
  sort_index?: number;
  appraisal_kgi_points?: AppraisalKgiPoint[];
};

export type AppraisalKpi = {
  id?: number;
  appraisal_id?: number;
  kpi_name?: string;
  kpi_note?: string;
  kpi_priority?: number;
  sort_index?: number;
  appraisal_kpi_points?: AppraisalKpiPoint[];
};

export type AppraisalKpiSheet = {
  id?: number;
  appraisal_kpi_id?: number;
  appraisal_kgi_id?: number;
  appraisal_kgi?: AppraisalKgi;
  appraisal_kpi?: AppraisalKpi;
  self_goal?: string;
  self_midterm_comment?: number;
  self_midterm_point?: number;
  self_end_term_comment?: number;
  self_end_term_point?: number;
  evaluators: AppraisalSheetDetailEvaluator[];
  feedbacks: {
    id?: number;
    appraisal_sheet_detail_id?: number;
    user_id?: number;
    comment?: string;
  }[];
};

export type AppraisalKgiSheet = {
  id?: number;
  appraisal_kgi_id?: number;
  self_goal?: string;
  self_midterm_comment?: number;
  self_midterm_point?: number;
  self_end_term_comment?: number;
  self_end_term_point?: number;
  evaluators: AppraisalSheetDetailEvaluator[];
  feedbacks: {
    id?: number;
    appraisal_sheet_detail_id?: number;
    user_id?: number;
    comment?: string;
  }[];
};

export type AppraisalKpiPoint = {
  id?: number;
  appraisal_kpi_id?: number;
  point_name?: string;
  point_value?: number;
};

export type AppraisalKgiPoint = {
  id?: number;
  appraisal_kgi_id?: number;
  point_name?: string;
  point_value?: number;
};

export type AppraisalSheet = {
  id?: number;
  revision?: string;
  appraisal_id?: number;
  appraisal_member_id?: number;
  appraisal_member?: AppraisalMember;
  appraisal?: Appraisal;
  approver_id?: number;
  status?:
    | string
    | "process_setting_goal"
    | "setting_goal_done"
    | "process_midterm"
    | "midterm_done"
    | "input_appraisal"
    | "appraisal_done";
  evaluator_statuses?: AppraisalSheetEvaluatorStatus[];
  evaluators?: AppraisalSheetDetailEvaluator[];
  appraisal_sheet_details?: AppraisalSheetDetail[];
  points?: any;
  reflect_previous_appraiser_comment?: boolean;
};

export type AppraisalAppraisere = {
  id?: number;
  appraisal_member_id?: number;
  worker_id?: number;
  order_number?: number;
  sort_index?: number;
};

export type AppraisalSheetEvaluatorStatus = {
  id?: number;
  appraisal_sheet_id?: number;
  worker_id?: number;
  resolver_id?: number;
  order_number?: number;
  status?: "waiting" | "approved" | "rejected"; //1: waiting, 2: approved, 3: rejected
  comment?: string;
  term?: "beginning" | "mid" | "end";
};

export type AppraisalSheetDetail = {
  id?: number;
  appraisal_sheet_id?: number;
  appraisal_kpi_id?: number;
  appraisal_kgi_id?: number;
  appraisal_kpi?: AppraisalKpi;
  appraisal_kgi?: AppraisalKgi;
  self_goal?: string;
  self_midterm_comment?: string;
  self_midterm_point?: number;
  self_end_term_comment?: string;
  self_end_term_point?: number;
  evaluators: AppraisalSheetDetailEvaluator[];
  feedbacks?: AppraisalSheetDetailFeedback[];
};

export type AppraisalSheetDetailFeedback = {
  id?: number;
  appraisal_sheet_detail_id?: number;
  worker_id?: number;
  comment?: string;
  term?: string;
  receiver_worker_id?: number;
  worker?: Worker;
  created_at?: Date;
};

export type AppraisalSheetDetailEvaluator = {
  id?: number;
  appraisal_sheet_detail_id?: number;
  order_number?: number;
  sort_index?: number;
  evaluator_comment?: string;
  evaluator_point?: number | null;
  worker_id?: number;
};

export const AppraisalSheetStatusText = {
  assigned_to_member: "目標設定中",
  member_seen: "目標設定中",
  process_setting_goal: "目標設定中",
  setting_goal_done: "目標設定完了",
  process_midterm: "中間面談中",
  midterm_done: "中間面談完了",
  input_appraisal: "評価入力",
  appraisal_done: "評価完了",
};

export const AppraisalSheetListStatusText = {
  assigned_to_member: "目標承認依頼中",
  member_seen: "目標承認依頼中",
  process_setting_goal: "目標承認依頼中",
  setting_goal_done: "目標設定完了",
  process_midterm: "中間面談中",
  midterm_done: "中間面談完了",
  input_appraisal: "評価入力",
  appraisal_done: "評価完了",
};
